// Single owner of the stored session token. Everything that reads or writes the
// bearer goes through here so the storage format and expiry rules live in one place.

const TOKEN_KEY = "access_token";

// Refresh this long before expiry so a slow request never goes out with a dead token.
const REFRESH_MARGIN_MS = 10 * 60 * 1000;

/** Stored as a JSON string for compatibility with sessions saved by older builds. */
export const getToken = (): string | null => {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed === "string" && parsed.split(".").length === 3) return parsed;
  } catch {
    // Fall through: a value we can't read is treated as no session.
  }
  clearToken();
  return null;
};

export const setToken = (token: unknown) => {
  if (typeof token !== "string" || token.split(".").length !== 3) {
    clearToken();
    return;
  }
  try { localStorage.setItem(TOKEN_KEY, JSON.stringify(token)); } catch {}
};

export const clearToken = () => {
  try { localStorage.removeItem(TOKEN_KEY); } catch {}
};

/** Expiry in epoch ms, read from the JWT payload. The signature is the server's job. */
export const tokenExpiry = (token: string | null): number | null => {
  if (!token) return null;
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(atob(payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), "=")));
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
};

/** The stored token, or null when there is none or it has already expired. */
export const getLiveToken = (): string | null => {
  const token = getToken();
  const exp = tokenExpiry(token);
  if (!token || !exp || exp <= Date.now()) return null;
  return token;
};

export const hasLiveSession = () => getLiveToken() !== null;

/** Milliseconds until the token should be refreshed (0 = now), or null without a session. */
export const msUntilRefresh = (): number | null => {
  const exp = tokenExpiry(getLiveToken());
  return exp ? Math.max(0, exp - Date.now() - REFRESH_MARGIN_MS) : null;
};

export const TOKEN_STORAGE_KEY = TOKEN_KEY;

/**
 * Only same-site relative paths are accepted as post-login destinations, so a crafted
 * `?next=` can't bounce a freshly signed-in user to another origin.
 */
export const safeNextPath = (next: string | null | undefined): string => {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return "/";
  if (/^\/(login|register|forgot)\b/i.test(next)) return "/";
  return next;
};
